import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Filter, X } from "lucide-react";
import { cn } from "@/utils/utils";

export interface TMultiSelectColumnFilterProps {
    /** Column label, shown in the popover heading and used for the aria-label. */
    label: string;
    /** Selectable options (typically the distinct values in the column). */
    options: { value: string; label: string }[];
    /** Currently selected option values. Empty = no filter (show all rows). */
    selected: string[];
    /** Called with the next selection whenever the user toggles an option or clears. */
    onChange: (next: string[]) => void;
}

// Case-insensitive substring match over option labels, to narrow long lists.
function filterOptionsByQuery(
    options: { value: string; label: string }[],
    query: string,
): { value: string; label: string }[] {
    const q = query.trim().toLowerCase();
    if (q === "") return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
}

/**
 * Excel-style column filter: a funnel button that opens a popover with a search
 * box and a checkbox list. Empty selection means "show all".
 *
 * Mirrors the multi-select filter used in the DataTable (Financial TB/GL Files
 * "Business Partner Name" column) but is decoupled from TanStack Table so it can
 * be dropped into plain/custom tables.
 */
export function MultiSelectColumnFilter({
    label,
    options,
    selected,
    onChange,
}: TMultiSelectColumnFilterProps) {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");

    const hasActiveFilter = selected.length > 0;
    const visibleOptions = React.useMemo(
        () => filterOptionsByQuery(options, query),
        [options, query],
    );

    const toggle = (value: string) => {
        const next = selected.includes(value)
            ? selected.filter((v) => v !== value)
            : [...selected, value];
        onChange(next);
    };

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className="p-0.5 rounded hover:bg-muted/80"
                    title="Filter"
                    aria-label={`Filter ${label}`}
                >
                    <Filter
                        className={cn(
                            "h-3.5 w-3.5 transition-opacity",
                            hasActiveFilter
                                ? "opacity-100 text-primary"
                                : "opacity-0 group-hover:opacity-100 text-muted-foreground",
                        )}
                    />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className="z-50 p-3 bg-popover border rounded-lg shadow-lg animate-in fade-in-0 zoom-in-95 min-w-[200px] max-w-[min(100vw-1rem,320px)] w-[min(100vw-1rem,280px)]"
                    sideOffset={5}
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                        Filter by {label}
                    </div>
                    <div className="flex min-w-0 flex-col space-y-2">
                        <div className="relative shrink-0">
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search options..."
                                className="w-full h-8 px-3 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/50"
                                autoFocus
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => setQuery("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
                                    aria-label="Clear search"
                                >
                                    <X className="h-3 w-3 text-muted-foreground" />
                                </button>
                            )}
                        </div>
                        <div className="flex max-h-[min(360px,55vh)] flex-col gap-2 overflow-y-auto overflow-x-hidden pr-1">
                            {visibleOptions.length === 0 ? (
                                <span className="py-1 text-sm text-muted-foreground">
                                    No matching options
                                </span>
                            ) : (
                                visibleOptions.map((option) => (
                                    <label
                                        key={option.value}
                                        className="flex cursor-pointer items-start gap-2 text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(option.value)}
                                            onChange={() => toggle(option.value)}
                                            className="border-input mt-0.5 shrink-0 rounded"
                                        />
                                        <span
                                            className="min-w-0 flex-1 break-words leading-snug"
                                            title={option.label}
                                        >
                                            {option.label}
                                        </span>
                                    </label>
                                ))
                            )}
                        </div>
                        <div className="flex shrink-0 justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => onChange([])}
                                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                    <Popover.Arrow className="fill-popover" />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
